import { useCallback, useContext, useEffect, useRef } from 'react';

import { DataContext, type GlyphProps, GlyphSeries, TooltipContext, type TooltipDatum } from '@visx/xychart';

import { XYChartRefContext } from '../XYChart';

export type FormatDataPointLabel<T extends object = object> = (datum: T, index: number, total: number) => string;

/**
 * Enables keyboard navigation for chart data points.
 *
 * Renders invisible interactive glyphs at each data point that can be focused and navigated
 * using keyboard controls. Supports arrow keys, Page Up/Down, Home, End, and Escape.
 * This component is automatically included when using the Cursor component.
 *
 * Keyboard controls:
 * - Arrow keys: Move to next/previous point
 * - Page Up/Down: Jump by 25% of data points
 * - Home/End: Jump to first/last point
 * - Escape: Close tooltip and return focus to chart
 * - Tab: Close tooltip and move focus to next element
 *
 * @example
 * ```tsx
 * <XYChart width={500} height={300}>
 *   <AreaSeries data={data} dataKey="series1" xAccessor={d => d.x} yAccessor={d => d.y} />
 *   <KeyboardNavigation
 *     formatDataPointLabel={(datum, index, total) =>
 *       `Point ${index + 1} of ${total}: ${datum.value}`
 *     }
 *   />
 * </XYChart>
 * ```
 */
export const KeyboardNavigation = <T extends object = object>({
  formatDataPointLabel,
}: {
  formatDataPointLabel: FormatDataPointLabel<T>;
}) => {
  const chartRef = useContext(XYChartRefContext);
  const { dataRegistry } = useContext(DataContext) ?? {};
  const tooltipContext = useContext(TooltipContext);
  const dataset = dataRegistry?.entries()[0];
  const glyphRefsMap = useRef<Map<number, SVGCircleElement>>(new Map());

  const focusGlyphAtIndex = useCallback(
    (index: number) => {
      if (!dataRegistry || dataset?.data[index] === undefined) return;

      const glyphElement = glyphRefsMap.current.get(index);
      if (glyphElement) {
        glyphElement.focus();

        const datumByKey = dataRegistry.keys().reduce<{ [key: string]: TooltipDatum<T> }>((datums, key) => {
          const datum = dataRegistry.get(key).data[index] as T;
          if (datum !== undefined) {
            datums[key] = {
              key,
              index,
              datum,
            };
          }
          return datums;
        }, {});

        // Use SVG DOM API, fallback to getAttribute for older browsers and test environment
        const tooltipLeft = glyphElement.cx?.baseVal?.value ?? parseFloat(glyphElement.getAttribute('cx') || '0');
        const tooltipTop = glyphElement.cy?.baseVal?.value ?? parseFloat(glyphElement.getAttribute('cy') || '0');

        tooltipContext?.updateTooltip({
          tooltipOpen: true,
          tooltipData: {
            datumByKey,
            nearestDatum: {
              datum: dataset.data[index],
              index,
              key: dataset.key,
              distance: 0,
            },
          },
          tooltipLeft,
          tooltipTop,
        });

        // Focus after tooltip update has been applied to DOM
        requestAnimationFrame(() => {
          glyphElement.focus();
        });
      }
    },
    [dataset, dataRegistry, tooltipContext]
  );

  useEffect(() => {
    const chartContainer = chartRef?.current;
    if (!chartContainer || !dataset) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target !== e.currentTarget) return;

      // On arrow keys or Enter, focus the first glyph to start navigation
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter'].includes(e.key)) {
        e.preventDefault();
        focusGlyphAtIndex(0);
      }
    };
    chartContainer.addEventListener('keydown', handleKeyDown);
    return () => {
      chartContainer?.removeEventListener('keydown', handleKeyDown);
    };
  }, [chartRef, dataset, focusGlyphAtIndex]);

  const handleGlyphKeyDown = useCallback(
    (e: React.KeyboardEvent, currentIndex: number) => {
      if (!dataset) return;

      const dataLength = dataset.data.length;
      const quarterJump = Math.max(1, Math.floor(dataLength / 4)); // 25% of dataset, minimum 1
      let newIndex: number | null = null;

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          newIndex = Math.max(0, currentIndex - 1);
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          newIndex = Math.min(dataLength - 1, currentIndex + 1);
          break;
        case 'PageUp':
          newIndex = Math.max(0, currentIndex - quarterJump);
          break;
        case 'PageDown':
          newIndex = Math.min(dataLength - 1, currentIndex + quarterJump);
          break;
        case 'Home':
          newIndex = 0;
          break;
        case 'End':
          newIndex = dataLength - 1;
          break;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          tooltipContext?.hideTooltip();
          chartRef?.current?.focus();
          return;
        case 'Tab':
          tooltipContext?.hideTooltip();
          return; // Don't preventDefault - let Tab work naturally
        default:
          return; // Unhandled key
      }

      // If we got a new index, move to it
      if (newIndex !== null) {
        e.preventDefault();
        e.stopPropagation();
        focusGlyphAtIndex(newIndex);
      }
    },
    [dataset, tooltipContext, chartRef, focusGlyphAtIndex]
  );

  const renderGlyph = useCallback<(props: GlyphProps<T>) => React.JSX.Element | null>(
    ({ key, x, y, datum, onBlur, onFocus, onPointerMove, onPointerOut, onPointerUp }) => {
      // TODO: use index from props when available in visx: https://github.com/airbnb/visx/pull/1978
      const index = Number(key);
      if (!dataset || Number.isNaN(index)) return null;

      return (
        <circle
          key={key}
          ref={el => {
            if (el) {
              glyphRefsMap.current.set(index, el);
            } else {
              glyphRefsMap.current.delete(index);
            }
          }}
          tabIndex={-1}
          aria-label={formatDataPointLabel(datum, index, dataset.data.length)}
          fill="transparent"
          cx={x}
          cy={y}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={e => handleGlyphKeyDown(e, index)}
          onPointerMove={onPointerMove}
          onPointerOut={onPointerOut}
          onPointerUp={onPointerUp}
        />
      );
    },
    [dataset, formatDataPointLabel, handleGlyphKeyDown]
  );

  return (
    dataset && (
      <GlyphSeries
        key={dataset.key}
        data={dataset.data}
        dataKey={`${dataset.key}-keyboard`}
        xAccessor={dataset.xAccessor}
        yAccessor={dataset.yAccessor}
        renderGlyph={renderGlyph}
      />
    )
  );
};
